import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Plus, ClipboardList, Building2, UserRound, AlertTriangle, RotateCw, Loader2 } from 'lucide-react';
import ToggleSwitch from '../common/ToggleSwitch';
import CheckSetCard from '../checks/CheckSetCard';
import CheckSetEditor from '../checks/CheckSetEditor';
import useSetActions from '../checks/useSetActions';
import { fetchCheckSets, fetchCheckSwitch, saveCheckSwitch } from '../../redux/checkSetSlice';
import { groupByMeantFor } from '../../utils/checkSets';
import { serverReason } from '../patientTabs/prescriptionHelpers';

// SETTINGS → CHECK SETS (doctor). The doctor's own switch, their own sets
// (create / edit / delete), and the hospital's sets (read + "Copy to my
// sets"). Every set and field is the doctor's own definition; nothing here is
// pre-made or speciality-specific (owner, 26 Sep 2026).
//
// The switch (prescriptionSections.checkSets, absent = off) decides whether
// the visit shows the Check readings block, whether the doctor's staff are
// offered the doctor's own sets, and whether "Required in every visit" gates
// completion. Readings already taken stay either way.
//
// The HOSPITAL ADMIN gets the same screen for any doctor of the hospital
// (spec §4, parity with the prescription-field rule): HospitalCheckSets
// passes `doctor` ({ doctorId, name }) and everything here then acts on that
// doctor — GET ?purpose=manage&doctorId=, a doctor set created with its
// doctorId, "Copy to this doctor's sets" with the doctorId, and that doctor's
// switch. The switch's state is the one the list answer carries (`enabled`),
// so the admin view asks nothing more. The server enforces who may.

function CheckSetsTab({ doctor = null }) {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();
    const forAdmin = Boolean(doctor?.doctorId);
    const doctorId = forAdmin ? doctor.doctorId : localStorage.getItem('ID');
    const doctorName = forAdmin ? (doctor.name || t('common.doctor_label', 'Doctor')) : '';
    // The doctor's own list is asked WITHOUT a doctorId (as before); the
    // admin's names the doctor.
    const scopeId = forAdmin ? doctorId : null;
    const { manage, switchState, busyId } = useSelector((state) => state.checkSets);
    const { remove, copy } = useSetActions();
    const [editor, setEditor] = useState({ open: false, set: null });

    const load = () => dispatch(fetchCheckSets(forAdmin ? { purpose: 'manage', doctorId: scopeId } : { purpose: 'manage' }));

    useEffect(() => {
        setEditor({ open: false, set: null });
        load();
        if (!forAdmin && doctorId) dispatch(fetchCheckSwitch({ doctorId }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, doctorId, forAdmin]);

    // The list on screen must be THIS doctor's (the store holds one at a time).
    const current = manage.purpose === 'manage' && manage.doctorId === scopeId;
    const loaded = current && manage.loaded;
    const loadError = current ? manage.error : null;

    let enabled;
    if (forAdmin) {
        // The list answer carries this doctor's switch; a save updates it.
        enabled = current && (typeof manage.enabled === 'boolean'
            ? manage.enabled
            : switchState.doctorId === doctorId && switchState.enabled === true);
    } else {
        enabled = switchState.doctorId === doctorId && switchState.enabled !== null
            ? switchState.enabled
            : manage.enabled === true;
    }
    const switchBusy = switchState.saving || (forAdmin ? !loaded : switchState.loading);

    const toggle = async () => {
        if (!doctorId || switchBusy) return;
        const res = await dispatch(saveCheckSwitch({ doctorId, enabled: !enabled }));
        if (saveCheckSwitch.fulfilled.match(res)) {
            const on = res.payload.enabled;
            toast.success(forAdmin
                ? t(on ? 'checkSets.doctor_switch_saved_on' : 'checkSets.doctor_switch_saved_off', { name: doctorName })
                : t(on ? 'checkSets.switch_saved_on' : 'checkSets.switch_saved_off'));
        } else {
            toast.error(serverReason(res.payload, t('checkSets.switch_failed'), i18n.language));
        }
    };

    const hospitalGroups = groupByMeantFor(manage.hospital);

    return (
        <div data-testid={forAdmin ? 'doctor-check-sets' : 'check-sets-tab'} className="space-y-6">
            {!forAdmin && (
                <div>
                    <h3 className="flex items-center gap-2 text-base font-semibold text-gray-800">
                        <ClipboardList className="h-5 w-5 text-[#4ABDBD]" aria-hidden="true" />
                        {t('checkSets.heading')}
                    </h3>
                    <p className="mt-1 text-sm text-gray-500">{t('checkSets.subheading')}</p>
                </div>
            )}

            <div data-testid="check-sets-switch-card" className="flex items-start justify-between gap-4 rounded-2xl border border-[#4ABDBD]/25 bg-gradient-to-r from-[#4ABDBD]/10 to-transparent p-4">
                <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-800">
                        {forAdmin ? t('checkSets.doctor_switch_title', { name: doctorName }) : t('checkSets.switch_title')}
                    </p>
                    <p className="mt-1 max-w-2xl text-xs text-gray-500">
                        {forAdmin ? t('checkSets.doctor_switch_hint') : t('checkSets.switch_hint')}
                    </p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                    {(switchState.saving || (!forAdmin && switchState.loading)) && <Loader2 className="h-4 w-4 animate-spin text-[#4ABDBD]" aria-hidden="true" />}
                    <span data-testid="check-sets-switch-state" className="text-xs font-semibold text-gray-500">
                        {enabled ? t('checkSets.switch_on') : t('checkSets.switch_off')}
                    </span>
                    <ToggleSwitch id="check-sets-enabled" on={enabled} onToggle={toggle} disabled={switchBusy} />
                </div>
            </div>

            {manage.loading && !loaded && (
                <div data-testid="check-sets-loading" className="flex items-center justify-center gap-2 py-8 text-sm text-[#4ABDBD]">
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                    {t('checkSets.loading')}
                </div>
            )}

            {loadError && (
                <div role="alert" data-testid="check-sets-load-error" className="flex flex-col items-center gap-2 rounded-xl border border-red-100 bg-red-50/60 px-4 py-6 text-center">
                    <p className="flex items-center gap-1.5 text-sm font-semibold text-red-700">
                        <AlertTriangle className="h-4 w-4" aria-hidden="true" />
                        {serverReason(loadError, t('checkSets.load_failed'), i18n.language)}
                    </p>
                    <button
                        type="button"
                        data-testid="check-sets-retry"
                        onClick={load}
                        className="inline-flex items-center gap-1.5 rounded-xl border border-red-200 bg-white px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-50"
                    >
                        <RotateCw className="h-3.5 w-3.5" aria-hidden="true" />
                        {t('checkSets.retry')}
                    </button>
                </div>
            )}

            {loaded && !loadError && (
                <>
                    <section data-testid="check-sets-mine">
                        <div className="mb-3 flex flex-wrap items-end justify-between gap-2">
                            <div className="min-w-0">
                                <h4 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-gray-400">
                                    <UserRound className="h-3.5 w-3.5" aria-hidden="true" />
                                    {forAdmin ? t('checkSets.doctor_sets', { name: doctorName }) : t('checkSets.my_sets')}
                                </h4>
                                <p className="text-xs text-gray-500">{forAdmin ? t('checkSets.doctor_sets_hint') : t('checkSets.my_sets_hint')}</p>
                            </div>
                            <button
                                type="button"
                                data-testid="check-sets-new"
                                onClick={() => setEditor({ open: true, set: null })}
                                className="inline-flex items-center gap-1.5 rounded-xl bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] px-4 py-2 text-sm font-semibold text-white shadow transition hover:shadow-md"
                            >
                                <Plus className="h-4 w-4" aria-hidden="true" />
                                {t('checkSets.new_set')}
                            </button>
                        </div>
                        {manage.mine.length === 0 ? (
                            <p data-testid="check-sets-mine-empty" className="rounded-xl bg-gray-50 px-4 py-6 text-center text-sm text-gray-400">{forAdmin ? t('checkSets.no_doctor_sets') : t('checkSets.no_my_sets')}</p>
                        ) : (
                            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
                                {manage.mine.map((set) => (
                                    <CheckSetCard
                                        key={set.setId}
                                        set={set}
                                        busy={busyId === set.setId}
                                        onEdit={(s) => setEditor({ open: true, set: s })}
                                        onDelete={remove}
                                    />
                                ))}
                            </div>
                        )}
                    </section>

                    <section data-testid="check-sets-hospital">
                        <h4 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-gray-400">
                            <Building2 className="h-3.5 w-3.5" aria-hidden="true" />
                            {t('checkSets.hospital_sets')}
                        </h4>
                        <p className="mb-3 text-xs text-gray-500">{forAdmin ? t('checkSets.hospital_sets_hint_admin') : t('checkSets.hospital_sets_hint')}</p>
                        {manage.hospital.length === 0 ? (
                            <p data-testid="check-sets-hospital-empty" className="rounded-xl bg-gray-50 py-6 text-center text-sm text-gray-400">{t('checkSets.no_hospital_sets')}</p>
                        ) : (
                            <div className="space-y-4">
                                {hospitalGroups.map((g) => (
                                    <div key={g.label || '_general'}>
                                        <p className="mb-2 text-xs font-semibold text-[#2D8A8A]">{g.label || t('checkSets.general_group')}</p>
                                        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
                                            {g.sets.map((set) => (
                                                <CheckSetCard
                                                    key={set.setId}
                                                    set={set}
                                                    busy={busyId === set.setId}
                                                    onCopy={(s) => (forAdmin ? copy(s, { doctorId }) : copy(s))}
                                                    copyLabel={forAdmin ? t('checkSets.copy_to_doctor') : null}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </section>
                </>
            )}

            <CheckSetEditor
                isOpen={editor.open}
                ownerType="doctor"
                doctorId={forAdmin ? doctorId : null}
                set={editor.set}
                onClose={() => setEditor({ open: false, set: null })}
            />
        </div>
    );
}

export default CheckSetsTab;
